import { useState } from 'react';
import './FaqSection.css';
import underlineImg from '../assets/underline.png';
import faqImg from '../assets/faq.png';
import { IconLightning } from './Icons';

const CATEGORIES = [
  { id: 'shopify',     label: 'Shopify' },
  { id: 'marketplaces', label: 'Marketplaces' },
  { id: 'seo',         label: 'SEO' },
  { id: 'marketing',   label: 'Marketing' },
  { id: 'about',       label: 'About MMC' },
];

const FAQS = {
  shopify: [
    { q: 'Does MMC provide Shopify development in Pune?', a: 'Yes — store setup, custom design, speed optimization, CRO, and Shopify SEO for D2C brands, fashion brands, startups, and marketplace sellers.' },
    { q: 'Why should Indian businesses choose Shopify?', a: 'Shopify offers fast performance, mobile optimization, easy inventory management, secure checkout, and scalable infrastructure — ideal for growing Indian brands.' },
    { q: 'Can MMC improve my Shopify store speed?', a: 'Absolutely. We optimize Core Web Vitals, image loading, and mobile performance. Faster stores rank higher on Google and convert significantly better.' },
    { q: 'Do you offer Shopify custom development?', a: 'Yes — custom themes, app integrations, checkout customization, and bespoke Shopify features built specifically around your brand\'s needs.' },
  ],
  marketplaces: [
    { q: 'Does MMC manage Amazon and Flipkart accounts?', a: 'Yes — full marketplace management including listing optimization, marketplace SEO, Amazon PPC, Flipkart advertising, catalog management, and a complete growth strategy.' },
    { q: 'Which marketplaces does MMC work with?', a: 'Amazon, Flipkart, Meesho, Myntra, and multi-channel setups. We also handle quick commerce channels like Blinkit and Zepto.' },
    { q: 'How does MMC improve marketplace rankings?', a: 'Through keyword-optimized listings, A+ content, review strategies, pricing intelligence, and targeted sponsored ads that push your products ahead of competitors.' },
  ],
  seo: [
    { q: 'What ecommerce SEO services does MMC offer?', a: 'Technical SEO, keyword research, Shopify SEO, product page optimization, blog SEO, speed optimization, and a full content strategy built for long-term organic traffic.' },
    { q: 'Why is ecommerce SEO critical for Indian businesses?', a: 'SEO reduces ad dependency and generates high-intent customers long-term. Ranking for terms like "Shopify experts India" drives qualified traffic without ongoing ad spend.' },
    { q: 'How long does ecommerce SEO take to show results?', a: 'Most clients see measurable improvements in 3–6 months. Technical fixes and on-page optimizations often show results faster; long-tail content compounds over time.' },
  ],
  marketing: [
    { q: 'Does MMC run performance marketing campaigns?', a: 'Yes — Meta ads, Google ads, ecommerce PPC, retargeting, funnel optimization, and ROAS optimization. We focus on profitability, not vanity metrics like impressions.' },
    { q: 'Why is conversion rate optimization important?', a: 'CRO increases sales without increasing ad spend. We optimize stores to reduce cart abandonment, improve checkout flow, and make every rupee of existing traffic work harder.' },
    { q: 'Can MMC help with D2C brand scaling?', a: 'Yes — through SEO, paid ads, Shopify optimization, CRO, marketplace growth, branding, and retention systems focused on sustainable long-term growth, not short-term spikes.' },
  ],
  about: [
    { q: 'Why is MMC considered one of the best ecommerce agencies in Pune?', a: 'MMC focuses purely on ecommerce growth — Shopify, marketplace management, SEO, and PPC. Every strategy is built around performance and long-term brand scaling.' },
    { q: 'What makes MMC different from other agencies in India?', a: 'We don\'t offer scattered digital services. MMC specializes end-to-end in ecommerce — giving brands a focused, efficient, and accountable path to scale.' },
    { q: 'Does MMC work with businesses outside Pune?', a: 'Yes. We work remotely with brands across Mumbai, Bangalore, Delhi, Hyderabad, Ahmedabad, and Chennai with a fully async collaboration process.' },
  ],
};

export default function FaqSection() {
  const [activeCat, setActiveCat] = useState('shopify');
  const [openIdx, setOpenIdx]     = useState(0);

  const questions = FAQS[activeCat] || [];

  const handleCat = (id) => {
    setActiveCat(id);
    setOpenIdx(0);
  };

  return (
    <section className="faq-sec">
      <div className="faq-inner">

        {/* ── LEFT ── */}
        <div className="faq-left">

          {/* Signature title block */}
          <div className="badge faq-badge">
            <span className="badge-icon"><IconLightning /></span>
            FAQ
          </div>
          <h2 className="faq-title">
            Questions we get
            <span className="faq-title-gradient"> answered</span>
          </h2>
          <img src={underlineImg} alt="" className="faq-underline" />
          <p className="faq-subtitle">
            Pick a topic and we'll answer the most important questions about that service.
          </p>

          {/* Category pills */}
          <div className="faq-pills">
            {CATEGORIES.map(({ id, label }) => (
              <button
                key={id}
                className={`faq-pill${activeCat === id ? ' faq-pill--on' : ''}`}
                onClick={() => handleCat(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Accordion */}
          <div className="faq-accordion">
            {questions.map((item, i) => {
              const open = openIdx === i;
              return (
                <div key={`${activeCat}-${i}`} className={`faq-row${open ? ' faq-row--open' : ''}`}>
                  <button className="faq-q" onClick={() => setOpenIdx(open ? null : i)}>
                    <span className="faq-q-text">{item.q}</span>
                    <span className="faq-chevron">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </span>
                  </button>
                  <div className="faq-a-wrap">
                    <p className="faq-a">{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="faq-foot">
            <span className="faq-foot-text">Still have questions?</span>
            <a href="#contact" className="faq-foot-link">
              Talk to our team
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </a>
          </div>
        </div>

        {/* ── RIGHT — FAQ image ── */}
        <div className="faq-right">
          <div className="faq-img-frame">
            <img src={faqImg} alt="FAQ illustration" className="faq-img" />
          </div>
        </div>

      </div>
    </section>
  );
}
